/**
 * Cart Drawer
 * Expects a #cart-icon-bubble trigger and a <cart-drawer> element.
 * Requires global helpers: trapFocus, removeTrapFocus, onKeyUpEscape.
 */

/* ----------------------- Free-shipping refresh helper ---------------------- */
/* Uses data attributes already rendered by Liquid. Safe if no bars exist.   */
function refreshFreeShippingBars(root) {
  const scope = root || document;
  scope
    .querySelectorAll('free-shipping-progress-bar, .free-shipping-container')
    .forEach((el) => {
      // Let the web component recompute itself if present
      if (customElements.get('free-shipping-progress-bar') && el.tagName === 'FREE-SHIPPING-PROGRESS-BAR') {
        if (typeof el.updateStatus === 'function') el.updateStatus();
        return;
      }

      // Lightweight fallback for plain .free-shipping-container markup
      const subtotal = Number(el.dataset.cartTotal || 0);
      const goal = Number(el.dataset.shippingAmount || 0);
      if (!goal) return;

      const remaining = Math.max(0, goal - subtotal);
      const eligible = remaining === 0;

      const remainingBox = el.querySelector('.free-shipping__info-remaining');
      const eligibleBox = el.querySelector('.free-shipping__info-eligibility');

      if (remainingBox) remainingBox.style.display = eligible ? 'none' : 'block';
      if (eligibleBox)  eligibleBox.style.display  = eligible ? 'block' : 'none';

      const percent = eligible ? 100 : Math.min(100, Math.floor((subtotal * 100) / goal));
      el.style.setProperty('--percent', percent + '%');
    });
}

document.addEventListener('DOMContentLoaded', () => refreshFreeShippingBars(document));

/* -------------------------------- CartDrawer ------------------------------- */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));

      this.setHeaderCartIconAccessibility();
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      // ✅ fix arrow-function typo and prevent default navigation
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });

      cartLink.addEventListener('keydown', (event) => {
        if (event.code && event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);

      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
        this.setSummaryAccessibility(cartDrawerNote);
      }

      // ensure animation triggers
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');

          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          if (typeof trapFocus === 'function') trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true }
      );

      document.body.classList.add('overflow-hidden');
    }

    close() {
      this.classList.remove('active');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');

      if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }

      cartDrawerNote.addEventListener('click', (event) => {
        const isOpen = event.currentTarget.closest('details').hasAttribute('open');
        event.currentTarget.setAttribute('aria-expanded', !isOpen);
      });

      if (typeof onKeyUpEscape === 'function') {
        cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
      }
    }

    renderContents(parsedState) {
      const inner = this.querySelector('.drawer__inner');
      if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');

      this.productId = parsedState.id;

      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;

        sectionElement.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
      });

      // 🔁 After re-rendering content, recompute free-shipping bars
      refreshFreeShippingBars(this);

      setTimeout(() => {
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
        this.open();
      });
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer',
        },
        {
          id: 'cart-icon-bubble',
        },
      ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

/* ----------------------------- CartDrawerItems ----------------------------- */
/* Extends theme's CartItems (provided elsewhere). Only overrides sections map */
if (!customElements.get('cart-drawer-items') && typeof CartItems !== 'undefined') {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}
